import type { EnrichedFeaturePermission } from "@dealer-center/ddc-pages-configuration-interface";
import {
  DEPARTMENT_GROUP_ID,
  applyDepartmentTemplate,
  departmentTypeToName
} from "./permissionLabel";

export interface PermissionRow {
  /** `EnrichedFeaturePermission.name` — CC9's code, and the label fallback. */
  code: string;
  /** `displayName ?? code`. Never blank (FR-033). */
  label: string;
  /** The row's own `granted` flag. Never inferred (FR-032). */
  isGranted: boolean;
}

export interface PermissionGroup {
  key: string;
  label: string;
  rows: PermissionRow[];
}

/*
 * `description` is absent from this view model on purpose.
 *
 * FR-032 asks for "each permission's human-readable name and its description where one exists", and
 * data-model §2 lists `description: string | null` — but `EnrichedFeaturePermission` carries no such
 * field, and neither does the CC9 response the BFF enriches (`{ name, access }`). So no description
 * ever exists, and a field that can only ever be null is noise that reads like an unimplemented
 * feature. The prototype shows descriptions only because its fixture data invents them.
 *
 * Recorded rather than worked around: if descriptions are wanted, they need a Label Services key
 * (`permission.resource.<name>.description` or similar) and BFF enrichment — a change to the lambda,
 * not to this file.
 */

const nonBlank = (value: string | null | undefined) =>
  value && value.trim() ? value : undefined;

/**
 * The localized copy this module needs, resolved by the caller (TA4197567).
 *
 * An object rather than a positional argument list — two loose strings plus a record at a call
 * site are trivially transposable.
 */
export interface PermissionLabels {
  /** The `CUSTOM_WEBSITE_DEPARTMENT` template, with its `{{label}}` token. */
  departmentTemplate: string;
  /**
   * Per-resource display names, keyed by CC9 permission code — how a resource points at its
   * label key. Used only when Label Services resolved no `permission.resource.<name>`, i.e. for
   * codes whose label was registered under a SCREAMING_SNAKE key instead.
   */
  resources: Record<string, string>;
}

/**
 * The display name for one row: Label Services' own value, else the custom-department template,
 * else a per-resource label, else the raw CC9 code (FR-033 — never a blank row).
 *
 * The department template is gated on `groupId`, not on the code's prefix alone:
 * `Website.Departments.Specials.FeaturedVehicles` matches that prefix but is a SmartSites row,
 * and naming it as a department would be wrong.
 */
const resolveLabel = (
  permission: EnrichedFeaturePermission,
  labels: PermissionLabels
) => {
  const resolved = nonBlank(permission.displayName);
  if (resolved) return resolved;

  if (permission.groupId === DEPARTMENT_GROUP_ID) {
    const departmentName = departmentTypeToName(permission.name);
    const template = nonBlank(labels.departmentTemplate);
    if (departmentName && template) {
      return applyDepartmentTemplate(template, departmentName);
    }
  }

  return nonBlank(labels.resources[permission.name]) ?? permission.name;
};

/**
 * Turns the flat `permissions[]` payload into the grouped view model the tab renders.
 *
 * Grouping key is `groupName ?? groupId` (data-model §2). Preferring the resolved name over the id
 * is safe as a *partition* key because `groupName` is derived deterministically from `groupId`
 * (Label Services `permission.group.<groupId>`), so both partition the rows identically — and it
 * means the key is already the string worth displaying.
 *
 * A row with **neither** is skipped. There is no catch-all group any more: the BFF drops every
 * ungrouped code before it returns (TA4197567 item 1), verified live at zero such rows on both QA
 * accounts, so a row reaching here with no group would be a contract violation rather than the
 * ~112-of-175 norm the old "Additional Permissions" bucket existed to hold.
 *
 * Rows **within** a group are sorted alphabetically on the label resolved here, which is not always
 * the label the BFF sorted on (TA4197567 item 7 — see the note at the sort). Groups are ordered
 * alphabetically by label.
 *
 * @param labels Localized copy, passed in rather than resolved here so this stays a pure function —
 * Configuration Principle VI still applies, it is just enforced at the call site, which is also what
 * makes the unit test independent of `labels.json`.
 */
export const groupPermissions = (
  permissions: EnrichedFeaturePermission[],
  labels: PermissionLabels
): PermissionGroup[] => {
  const groups = new Map<string, PermissionGroup>();

  permissions.forEach(permission => {
    const key = nonBlank(permission.groupName) ?? nonBlank(permission.groupId);
    if (!key) return;

    let group = groups.get(key);
    if (!group) {
      group = { key, label: key, rows: [] };
      groups.set(key, group);
    }

    group.rows.push({
      code: permission.name,
      label: resolveLabel(permission, labels),
      // FR-032: read the row's own flag. NOT its presence (every catalog row is present, granted or
      // not) and NOT `access`, which is null on an ungranted row and would read as falsy by accident.
      isGranted: permission.granted
    });
  });

  /*
   * `Array.from(map.values())`, never `[...map.values()]`. The repo typechecks at the ES5 default —
   * `tsconfig.base.json` sets no `target` and no `downlevelIteration` — so spreading a Map iterator
   * is a hard **TS2802** error. It would not fail `test`, because ts-jest uses its own transform;
   * only `:typecheck` runs `tsc --build` (research R-011, and T038 is the explicit check).
   */
  return Array.from(groups.values())
    .map(group => {
      /*
       * Rows are re-sorted on the label THIS function resolved, not left in payload order
       * (TA4197567 item 7).
       *
       * The BFF already sorts alphabetically — but it sorts on `displayName ?? name`, and for
       * every row that reaches a fallback above, the label it sorted on is not the label
       * rendered. Verified live on `crmtestaudi`: the department group arrived as Body Shop,
       * Employment, Financing, Parts, Service, `…EXOTIC_FLEET`, `…WHOLESALE_FLEET` — correct by
       * code, but once the last two are named "Exotic Fleet Department" and "Wholesale Fleet
       * Department" they belong second and last. `ddcdemo` has nine such departments, all
       * bunched after the labeled ones.
       *
       * So this sort is not redundant with the BFF's; it is the only one that sees the final
       * strings. The BFF's still matters for any consumer that renders `displayName` directly.
       */
      group.rows.sort((left, right) => left.label.localeCompare(right.label));
      return group;
    })
    .sort((left, right) => left.label.localeCompare(right.label));
};
